/**
 * Module-level drag state, accessed without zustand to avoid per-frame
 * React re-renders.
 *
 * Two flags:
 *   - `isPressed`: pointer button is currently held. Flips on pointerdown,
 *     clears (deferred one tick) on pointerup. Used by *hover* handlers
 *     so cursor sweeps during a press-and-hold gesture don't trigger
 *     hover cards / sleeve expansions / preview state, even before any
 *     significant movement has accumulated.
 *   - `isDragging`: as above, but only set once movement exceeds
 *     DRAG_DISTANCE_THRESHOLD. Used by *click* handlers so a plain tap
 *     still fires while a drag-end doesn't.
 *
 * isDragging is a strict subset of isPressed in the time domain.
 */

const DRAG_DISTANCE_THRESHOLD = 5;

export const dragState = {
  /** True any time a pointer button is held down (any movement). */
  isPressed: false,
  /** True only after movement during a press exceeds the threshold. */
  isDragging: false,
};

let pointerDown = false;
let startX = 0;
let startY = 0;
let totalDist = 0;

function onPointerDown(e: PointerEvent) {
  pointerDown = true;
  startX = e.clientX;
  startY = e.clientY;
  totalDist = 0;
  dragState.isPressed = true;
  dragState.isDragging = false;
}

function onPointerMove(e: PointerEvent) {
  if (!pointerDown) return;
  totalDist = Math.hypot(e.clientX - startX, e.clientY - startY);
  if (totalDist > DRAG_DISTANCE_THRESHOLD && !dragState.isDragging) {
    dragState.isDragging = true;
    // One-shot signal at the moment a press becomes a drag — components
    // that need to clear "stale" hover/preview state at the start of a
    // pan listen for this. We DON'T fire on plain pointerdown because
    // doing so would also clear hover state during a click (which is
    // just a no-movement press → release), breaking interactions like
    // clicking an expanded sleeve sub-arc.
    window.dispatchEvent(new CustomEvent('bjjmap:dragstart'));
  }
}

function onPointerUp() {
  pointerDown = false;
  // Defer the reset by one tick so a click handler firing as part of
  // the same pointerup sequence still sees both flags and can bail.
  setTimeout(() => {
    dragState.isPressed = false;
    dragState.isDragging = false;
  }, 0);
}

let installed = false;

export function installDragListeners(): () => void {
  if (installed) return () => {};
  installed = true;
  window.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);
  return () => {
    installed = false;
    window.removeEventListener('pointerdown', onPointerDown);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  };
}
